import { useState } from 'react';
import { Dialog, Tabs } from '@round/creatorbase-ui';
import styles from './BulkAddCreatorsModal.module.css';
import { BulkAddCreatorsTab, Screen } from './types';
import PasteUrls from './components/PasteUrls/PasteUrls';
import ReviewCreators from './components/ReviewCreators/ReviewCreators';
import BulkUploads from './components/BulkUploads/BulkUploads';
import BulkUploadResults from './components/BulkUploadResults/BulkUploadResults';
import SubScreen from './components/SubScreen/SubScreen';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    defaultTab?: BulkAddCreatorsTab;
    /** Opens on this upload's results instead of on a tab, with Back leading to the Results tab. */
    defaultBulkUploadId?: number;
};

const getTabScreen = (tab: BulkAddCreatorsTab): Screen => (tab === 'add' ? { name: 'paste' } : { name: 'bulkUploads' });

const getDefaultScreen = (defaultTab: BulkAddCreatorsTab, defaultBulkUploadId: number | undefined): Screen =>
    typeof defaultBulkUploadId === 'number'
        ? { name: 'bulkUploadResults', bulkUploadId: defaultBulkUploadId }
        : getTabScreen(defaultTab);

const BulkAddCreatorsModal = ({ isOpen, onClose, defaultTab = 'add', defaultBulkUploadId }: Props) => {
    const [screen, setScreen] = useState<Screen>(() => getDefaultScreen(defaultTab, defaultBulkUploadId));

    const handleClose = () => {
        onClose();
        setScreen(getDefaultScreen(defaultTab, defaultBulkUploadId));
    };

    const renderScreen = () => {
        if (screen.name === 'review') {
            return (
                <SubScreen backLabel="Paste URLs" onBack={() => setScreen({ name: 'paste' })} title="Review creators">
                    <ReviewCreators
                        urls={screen.urls}
                        onCreated={(bulkUploadId) => setScreen({ name: 'bulkUploadResults', bulkUploadId })}
                    />
                </SubScreen>
            );
        }

        if (screen.name === 'bulkUploadResults') {
            return (
                <SubScreen
                    backLabel="Results"
                    onBack={() => setScreen({ name: 'bulkUploads' })}
                    title="Bulk upload results"
                >
                    <BulkUploadResults bulkUploadId={screen.bulkUploadId} />
                </SubScreen>
            );
        }

        const activeTab: BulkAddCreatorsTab = screen.name === 'paste' ? 'add' : 'results';

        return (
            <div className={styles.screen}>
                <Dialog.Header>
                    <Dialog.Title>Bulk add creators</Dialog.Title>
                </Dialog.Header>

                <Tabs
                    orientation="horizontal"
                    value={activeTab}
                    onValueChange={(value) => setScreen(getTabScreen(value as BulkAddCreatorsTab))}
                    className={styles.tabs}
                >
                    <Tabs.List variant="line" className={styles.tabsList}>
                        <Tabs.Trigger value="add">Add</Tabs.Trigger>
                        <Tabs.Trigger value="results">Results</Tabs.Trigger>
                    </Tabs.List>

                    <Tabs.Content value="add" className={styles.tabContent}>
                        <PasteUrls onAdd={(urls) => setScreen({ name: 'review', urls })} />
                    </Tabs.Content>

                    <Tabs.Content value="results" className={styles.tabContent}>
                        <BulkUploads
                            onSelectBulkUpload={(bulkUploadId) =>
                                setScreen({ name: 'bulkUploadResults', bulkUploadId })
                            }
                        />
                    </Tabs.Content>
                </Tabs>
            </div>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <Dialog.Content className={styles.modalContent}>{renderScreen()}</Dialog.Content>
        </Dialog>
    );
};

export default BulkAddCreatorsModal;
